import React, { useCallback, useEffect, useState } from 'react';
import { Mail } from 'lucide-react';

import { useAuth } from '../../../contexts/AuthContext';
import logger from '../../../logging/logger';
import { NamePromptModal } from '../dashboards/NamePromptModal';
import type { MarketMonitorDashboardState } from '../dashboards/MarketMonitorDashboardProvider';
import { sendDashboardEmail } from './emailApi';
import { SendEmailModal } from './SendEmailModal';
import styles from './SendEmailButton.module.css';

type Toast =
  | { kind: 'success'; message: string }
  | { kind: 'error'; message: string }
  | null;

interface SaveAsPrompt {
  open: boolean;
  initialValue: string;
}

export interface SendEmailButtonProps {
  state: MarketMonitorDashboardState;
}

export const SendEmailButton: React.FC<SendEmailButtonProps> = ({ state }) => {
  const { userUuid, userEmail, getToken } = useAuth();
  const {
    activeId,
    activeName,
    isDirty,
    isSaving,
    isLoadingActive,
    save,
    saveAs,
  } = state;

  const [emailModalOpen, setEmailModalOpen] = useState<boolean>(false);
  const [confirmSaveOpen, setConfirmSaveOpen] = useState<boolean>(false);
  const [saveAsPrompt, setSaveAsPrompt] = useState<SaveAsPrompt>({ open: false, initialValue: '' });
  const [toast, setToast] = useState<Toast>(null);

  useEffect(() => {
    if (!toast) return undefined;
    const id = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(id);
  }, [toast]);

  const openEmailModal = useCallback(() => setEmailModalOpen(true), []);

  const handleClick = useCallback(() => {
    if (!userUuid) {
      setToast({ kind: 'error', message: 'Sign in is required to send emails.' });
      return;
    }
    if (!activeId) {
      setSaveAsPrompt({ open: true, initialValue: 'My Dashboard' });
      return;
    }
    if (isDirty) {
      setConfirmSaveOpen(true);
      return;
    }
    openEmailModal();
  }, [userUuid, activeId, isDirty, openEmailModal]);

  const handleSaveAsConfirm = useCallback(
    async (name: string) => {
      setSaveAsPrompt({ open: false, initialValue: '' });
      const result = await saveAs(name);
      if (result) openEmailModal();
    },
    [saveAs, openEmailModal],
  );

  const handleConfirmSave = useCallback(async () => {
    setConfirmSaveOpen(false);
    const result = await save();
    if (result) openEmailModal();
  }, [save, openEmailModal]);

  const handleSend = useCallback(
    async (subject: string, to: string) => {
      if (!userUuid || !activeId) {
        throw new Error('Cannot send: dashboard is not saved.');
      }
      const token = await getToken();
      try {
        await sendDashboardEmail(userUuid, token, activeId, subject, to);
        setEmailModalOpen(false);
        setToast({ kind: 'success', message: 'Email queued — should land in a minute.' });
      } catch (caught: unknown) {
        logger.error(`SendEmailButton: send failed — ${caught instanceof Error ? caught.message : caught}`);
        throw caught instanceof Error ? caught : new Error('Failed to send email.');
      }
    },
    [userUuid, activeId, getToken],
  );

  const defaultSubject = activeName
    ? `${activeName} — Market Monitor snapshot`
    : 'Market Monitor snapshot';

  const buttonDisabled = isSaving || isLoadingActive;

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        disabled={buttonDisabled}
        title="Email this dashboard"
        data-testid="send-email-button"
      >
        <Mail size={14} aria-hidden="true" />
        <span>Send email</span>
      </button>

      <NamePromptModal
        open={saveAsPrompt.open}
        title="Save dashboard before sending"
        label="Dashboard name"
        initialValue={saveAsPrompt.initialValue}
        confirmLabel="Save & continue"
        onCancel={() => setSaveAsPrompt({ open: false, initialValue: '' })}
        onConfirm={(name) => { void handleSaveAsConfirm(name); }}
      />

      {confirmSaveOpen ? (
        <ConfirmSaveDialog
          dashboardName={activeName ?? 'this dashboard'}
          onCancel={() => setConfirmSaveOpen(false)}
          onConfirm={handleConfirmSave}
        />
      ) : null}

      <SendEmailModal
        open={emailModalOpen}
        defaultSubject={defaultSubject}
        defaultTo={userEmail ?? ''}
        onCancel={() => setEmailModalOpen(false)}
        onSend={handleSend}
      />

      {toast ? (
        <div
          className={`${styles.toast} ${toast.kind === 'success' ? styles.toastSuccess : styles.toastError}`}
          role="status"
          data-testid={`send-email-toast-${toast.kind}`}
        >
          {toast.message}
        </div>
      ) : null}
    </>
  );
};

interface ConfirmSaveDialogProps {
  dashboardName: string;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}

const ConfirmSaveDialog: React.FC<ConfirmSaveDialogProps> = ({
  dashboardName,
  onCancel,
  onConfirm,
}) => {
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) onCancel();
  };
  return (
    <div
      className={styles.confirmBackdrop}
      onMouseDown={handleBackdropClick}
      role="presentation"
      data-testid="send-email-confirm-save"
    >
      <div
        className={styles.confirmModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="send-email-confirm-save-title"
      >
        <h2 id="send-email-confirm-save-title" className={styles.confirmTitle}>
          Unsaved changes
        </h2>
        <p className={styles.confirmBody}>
          {`Save changes to "${dashboardName}" before sending the email?`}
        </p>
        <div className={styles.confirmActions}>
          <button
            type="button"
            className={styles.confirmSecondary}
            onClick={onCancel}
            data-testid="send-email-confirm-save-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.confirmPrimary}
            onClick={() => { void onConfirm(); }}
            data-testid="send-email-confirm-save-confirm"
          >
            Save & continue
          </button>
        </div>
      </div>
    </div>
  );
};
